import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { serverError } from "../payments/register/registerPaymentModal";
import { ExencionesDeMatriculaDTO, MesDeMatricula, VistaPreviaExencion } from "../../../models/ExencionDTO";
import { fechaHoraAmPm } from "../../../utils/fechaHoraAmPm";
import "../../enrollment/rotativaPicker.css";
import { useEnviarAprobacion } from "../../aprobaciones/enviarAprobacion";

/*
	Eximir meses desde el Estado de Cuenta (Esteban, 2026-10-28): actualizaciones y cambios de horario, y las matrículas que ya
	nacieron con meses que no cursó. Solo Supervisión de Caja del plantel y superadmin (el servidor lo vuelve a revisar). Los
	meses eximidos dejan de deberse; los acuerdos se rehacen sobre los meses que quedan. Todo va a Auditoría y se puede deshacer.
	Actualizaciones v2 (2026-10-29): un mes Pagado o con abono también se exime; su dinero pasa a los meses pendientes de la matrícula
	(el más antiguo primero) o se queda como Pagado no cursado. Antes de confirmar, «Revisar» enseña exactamente qué pasará (el servidor
	corre la exención completa y la deshace). Los motivos llegan del servidor: solo los que aplican a esa matrícula.
*/
export interface EximirMesesModalProps {
	campusId: number;
	studentId: number;
	enrollmentId: number;
	courseName: string;
	currencySymbol: string;
	onClose: () => void;
	/** Algo cambió: recargar el Estado de Cuenta. */
	onChanged: () => void;
}

const MIN_TEXTO = 10;
const money = (n: number, s: string) => s + Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Lote = NonNullable<MesDeMatricula["exemption"]> & { meses: string[] };

const EximirMesesModal: React.FC<EximirMesesModalProps> = ({ campusId, studentId, enrollmentId, courseName, currencySymbol, onClose, onChanged }) => {
	const http = HttpManager.getInstance();
	const [data, setData] = useState<ExencionesDeMatriculaDTO | null>(null);
	const [elegidos, setElegidos] = useState<number[]>([]);
	const [motivo, setMotivo] = useState<string>("");
	const [explicacion, setExplicacion] = useState<string>("");
	const [deshaciendo, setDeshaciendo] = useState<string | null>(null);
	const [nota, setNota] = useState<string>("");
	const [error, setError] = useState<string | null>(null);
	/** Acuerdos armados a mano (referido, 2x1, cambio de grupo) que tocaban esos meses: no se rehacen solos. */
	const [aviso, setAviso] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	/** Lo que hará eximir (vista previa del servidor); cualquier cambio en la selección obliga a revisar otra vez. */
	const [previa, setPrevia] = useState<VistaPreviaExencion | null>(null);

	useEffect(() => {
		http.financesService.exemptions(campusId, studentId, enrollmentId)
			.then((r) => setData(r.data))
			.catch((e) => setError(serverError(e)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, studentId, enrollmentId]);

	const correr = async (fn: () => Promise<any>) => {
		try {
			setBusy(true);
			setError(null);
			const r = await fn();
			setData(r.data);
			const sinRehacer: string[] = (r.data as any)?.done?.acuerdos?.sinRehacer || [];
			setAviso(sinRehacer.length > 0 ? "Revisa a mano «" + sinRehacer.join("», «") + "»: es un acuerdo armado a mano y no se reacomodó solo (quedó anotado en Auditoría)." : null);
			setElegidos([]);
			setMotivo("");
			setExplicacion("");
			setPrevia(null);
			setDeshaciendo(null);
			setNota("");
			onChanged();
		} catch (e: any) {
			setError(serverError(e));
			// 409: algo cambió desde Revisar; la vista previa ya no vale y hay que revisar otra vez
			if (e?.response?.status === 409) { setPrevia(null); }
		} finally {
			setBusy(false);
		}
	};
	const cuerpo = () => ({ enrollmentId, tuitionIds: elegidos, reason: motivo, explanation: explicacion.trim() });
	const eximir = () => correr(() => http.financesService.exempt(campusId, studentId, { ...cuerpo(), previewHash: previa?.hash }));
	const revisar = async () => {
		try {
			setBusy(true);
			setError(null);
			setPrevia((await http.financesService.previewExemption(campusId, studentId, cuerpo())).data);
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};
	const deshacer = (batch: string) => correr(() => http.financesService.undoExemption(campusId, studentId, { enrollmentId, batch, note: nota.trim() }));
	/** Caja del plantel no exime sola (fase 2): arma los meses, ve lo que pasa y queda Por Aprobar para Supervisión de Caja. */
	const aprobacion = useEnviarAprobacion(campusId);
	const pedirAprobacion = () => aprobacion.pedir({
		kind: "eximir_meses", titulo: "Eximir Meses", motivoInicial: explicacion.trim(),
		data: { studentId, ...cuerpo() },
		onDone: () => onChanged(),
		onCerrar: (r) => { if (r) onClose(); },
	});
	const alternar = (m: MesDeMatricula) => {
		if (!m.canExempt || busy) return;
		setElegidos((l) => l.includes(m.tuitionId) ? l.filter((x) => x !== m.tuitionId) : [...l, m.tuitionId]);
		setPrevia(null);
	};
	const enMes = (m: MesDeMatricula) => m.exemption ? "Eximido" : m.why ? m.why
		: m.paid > 0.009 ? (m.paid + 0.009 >= m.subTotal ? "Pagado " : "Abono ") + money(m.paid, currencySymbol) : money(m.subTotal, currencySymbol);

	const eximidos = (data?.months || []).filter((m) => m.exemption);
	const lotes = Object.values(eximidos.reduce((acc, m) => {
		const x = m.exemption!;
		if (!acc[x.batch]) {
			acc[x.batch] = { ...x, movedTo: [], meses: [] };
		}
		acc[x.batch].meses.push(m.title);
		acc[x.batch].movedTo = [...(acc[x.batch].movedTo || []), ...(x.movedTo || [])];
		return acc;
	}, {} as Record<string, Lote>));
	const elegidosMeses = (data?.months || []).filter((m) => elegidos.includes(m.tuitionId));
	const monto = elegidosMeses.reduce((s, m) => s + Math.max(0, m.subTotal - (m.paid || 0)), 0);
	const pagado = elegidosMeses.reduce((s, m) => s + (m.paid || 0), 0);
	const listo = elegidos.length > 0 && motivo !== "" && explicacion.trim().length >= MIN_TEXTO;

	return (
		<>
		<Modal className="waModulo" show onHide={onClose} size="lg" centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>Eximir Meses: {courseName}</Modal.Title></Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 14 }}>
				{error ? <div className="alert alert-danger">{error}</div> : null}
				{aviso ? <div className="alert alert-warning">{aviso}</div> : null}
				{data === null ? (error ? null : <div className="loader" />) : (
					<>
						<div style={{ fontWeight: 700, marginBottom: 4 }}>Meses</div>
						<div className="waRotativaOpciones" style={{ marginTop: 0 }}>
							{data.months.map((m) => (
								<button key={"m" + m.tuitionId} type="button" className="waPildora waPildoraMes" aria-pressed={elegidos.includes(m.tuitionId) || !!m.exemption}
									disabled={busy || !m.canExempt} title={m.why || undefined} onClick={() => alternar(m)}>
									{m.title}
									<small>{enMes(m)}</small>
								</button>
							))}
						</div>
						<div style={{ fontWeight: 700, margin: "14px 0 4px" }}>Motivo</div>
						<div className="waRotativaOpciones" style={{ marginTop: 0 }}>
							{data.reasons.map((r) => (
								<button key={"r" + r.key} type="button" className="waPildora" aria-pressed={motivo === r.key} disabled={busy}
									onClick={() => { setMotivo(r.key); setPrevia(null); }}>{r.name}</button>
							))}
						</div>
						<textarea className="form-control" rows={2} style={{ marginTop: 12 }} placeholder="Explicación (va a Auditoría)" value={explicacion} disabled={busy}
							onChange={(e) => { setExplicacion(e.target.value); setPrevia(null); }} />
						{!data.canManage ? (
							<div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
								<button type="button" className="btn btn-primary btn-sm" disabled={busy || !listo} onClick={pedirAprobacion}>Enviar a Aprobación</button>
							</div>
						) : previa ? (
							<div style={{ border: "1px solid var(--wa-filo)", borderRadius: 8, padding: 10, marginTop: 12 }}>
								<div style={{ fontWeight: 700, marginBottom: 4 }}>Qué Va a Pasar</div>
								<div>{previa.titles.join(", ")}: {money(previa.amount, currencySymbol)} dejan de deberse</div>
								{previa.money.sources.map((src) => (
									<div key={"s" + src.tuitionId} style={{ marginTop: 6 }}>
										<b>{src.title}</b> ({money(src.paid, currencySymbol)} pagados)
										{src.moves.map((mv, i) => <div key={i} style={{ paddingLeft: 12 }}>Pasan a {mv.toTitle}: {money(mv.amount, currencySymbol)}</div>)}
										{src.remainder > 0.009 ? <div style={{ paddingLeft: 12 }}>Se quedan como Pagado No Cursado: {money(src.remainder, currencySymbol)}</div> : null}
									</div>
								))}
								{previa.acuerdos.sinRehacer.length > 0 ? (
									<div className="alert alert-warning" style={{ margin: "8px 0 0" }}>Revisa a mano «{previa.acuerdos.sinRehacer.join("», «")}»: es un acuerdo armado a mano y no se reacomoda solo.</div>
								) : null}
								<div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
									<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setPrevia(null)}>Atrás</button>
									<button type="button" className="btn btn-primary btn-sm" disabled={busy || !listo} onClick={eximir}>Confirmar</button>
								</div>
							</div>
						) : (
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
								<span style={{ color: "var(--wa-apagado)", fontSize: 13 }}>
									{elegidos.length < 1 ? null : `${elegidos.length} ${elegidos.length === 1 ? "mes" : "meses"}: ${money(monto, currencySymbol)} dejan de deberse`
										+ (pagado > 0.009 ? `, ${money(pagado, currencySymbol)} pagados se mueven` : "")}
								</span>
								<button type="button" className="btn btn-primary btn-sm" disabled={busy || !listo} onClick={revisar}>Revisar</button>
							</div>
						)}
						{lotes.length > 0 ? (
							<>
								<div style={{ fontWeight: 700, margin: "18px 0 4px" }}>Ya Eximidos</div>
								{lotes.map((l) => (
									<div key={l.batch} style={{ border: "1px solid var(--wa-filo)", borderRadius: 8, padding: 10, marginTop: 8 }}>
										<div><b>{l.meses.join(", ")}</b>: {l.reason}</div>
										<div style={{ whiteSpace: "pre-wrap" }}>{l.explanation}</div>
										{(l.movedTo || []).map((o, i) => <div key={i} style={{ fontSize: 13 }}>{o.text}: {money(o.amount, currencySymbol)}</div>)}
										<div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>{l.by}, {fechaHoraAmPm(l.at)}, {l.origin === "matricula" ? "al aprobar la Matrícula" : "desde el Estado de Cuenta"}</div>
										{!data.canManage ? null : deshaciendo === l.batch ? (
											<div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
												<input className="form-control form-control-sm" style={{ flex: "1 1 220px" }} autoFocus placeholder="Por qué se deshace" value={nota} onChange={(e) => setNota(e.target.value)} />
												<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setDeshaciendo(null)}>Atrás</button>
												<button type="button" className="btn btn-warning btn-sm" disabled={busy || nota.trim().length < MIN_TEXTO} onClick={() => deshacer(l.batch)}>Deshacer</button>
											</div>
										) : (
											<div style={{ textAlign: "right", marginTop: 6 }}>
												<button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0 }} disabled={busy} onClick={() => { setDeshaciendo(l.batch); setNota(""); }}>Deshacer</button>
											</div>
										)}
									</div>
								))}
							</>
						) : null}
					</>
				)}
			</Modal.Body>
		</Modal>
		{aprobacion.modal}
		</>
	);
};

export default EximirMesesModal;
